import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { RootState } from '../../../store';
import {
  useAdminAtualizarFormaPagamentoMutation,
  useAdminCriarFormaPagamentoMutation,
  useAdminListarFormasPagamentoQuery,
  useAdminListarFormasPagamentoSischefQuery,
  useAdminDiagnosticoPixQuery,
  useAdminListarIntegracoesPagamentoQuery,
  useAdminObterConfiguracaoQuery,
  type Canal,
  type FormaPagamentoLoja,
  type FormaPagamentoOpcao,
  type TipoPagamentoLoja,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { InstrucoesWebhookAsaas } from '../../../components/admin/instrucoes-webhook-asaas';
import { AlertaSemIntegracaoPagamento } from '../../../components/admin/alerta-sem-integracao-pagamento';
import { AlertaChavePix } from '../../../components/admin/alerta-chave-pix';
import { CamposPixEstatico, MAX_NOME_RECEBEDOR_PIX, type DadosPixEstatico } from '../../../components/admin/campos-pix-estatico';
import { InstrucoesTefElgin } from '../../../components/admin/instrucoes-tef-elgin';
import { PaginacaoTabela, useUltimaPagina } from '../../../components/admin/paginacao-tabela';
import { SnackbarFeedback, useFeedback } from '../../../components/admin/feedback';

const ROTULO_TIPO: Record<TipoPagamentoLoja, string> = {
  LOCAL: 'Pagamento local',
  ONLINE: 'Pagamento online',
  TEF: 'TEF (maquininha)',
};

const FORMAS: FormaPagamentoOpcao[] = ['DINHEIRO', 'CARTAO', 'PIX', 'OUTROS'];

/** Canais do cardápio onde a forma pode aparecer (default: todos marcados). */
const CANAIS: { valor: Canal; rotulo: string }[] = [
  { valor: 'CARDAPIO_DELIVERY', rotulo: 'Delivery' },
  { valor: 'CARDAPIO_TOTEM', rotulo: 'Totem de Pedidos' },
  { valor: 'CARDAPIO_TOTEM_CHECKOUT', rotulo: 'Totem de Checkout' },
  { valor: 'CARDAPIO_COMANDA_MESA', rotulo: 'Comanda/Mesa' },
  { valor: 'CARDAPIO_VITRINE', rotulo: 'Vitrine' },
];
const TODOS_CANAIS = CANAIS.map((c) => c.valor);

/** Flags "[A],[B]" -> ['A','B']; vazio/nulo = todos os canais (default/retrocompatível). */
function parseCanais(flags: string | null | undefined): Canal[] {
  if (!flags || !flags.trim()) return [...TODOS_CANAIS];
  const marcados = flags
    .split(',')
    .map((f) => f.replace(/[[\]]/g, '').trim())
    .filter(Boolean);
  const selecionados = TODOS_CANAIS.filter((c) => marcados.includes(c));
  return selecionados.length > 0 ? selecionados : [...TODOS_CANAIS];
}

/** Canais selecionados -> "[A],[B]" (na ordem canônica). */
function montarVisibilidadeFlags(canais: Canal[]): string | null {
  if (canais.length === 0) return null;
  return TODOS_CANAIS.filter((c) => canais.includes(c))
    .map((c) => `[${c}]`)
    .join(',');
}

/** Métodos oferecidos por tipo integrado (vira o CSV `metodos`). */
const METODOS_POR_TIPO: Record<'ONLINE' | 'TEF', { valor: string; rotulo: string }[]> = {
  ONLINE: [
    { valor: 'PIX', rotulo: 'PIX' },
    { valor: 'CREDIT_CARD', rotulo: 'Cartão de crédito' },
  ],
  TEF: [
    { valor: 'PIX', rotulo: 'PIX' },
    { valor: 'CREDIT_CARD', rotulo: 'Crédito' },
    { valor: 'DEBIT_CARD', rotulo: 'Débito' },
  ],
};

interface EstadoEditor {
  modo: 'criar' | 'editar';
  /** Preenchido só no modo editar — usado pra `PATCH /admin/formas-pagamento/:id`. */
  formaPagamento?: FormaPagamentoLoja;
  tipo: TipoPagamentoLoja;
  descricao: string;
  forma: FormaPagamentoOpcao;
  /** ID da forma de pagamento associada no Sischef (string pro select). */
  codigo: string;
  /** Config de integração escolhida (ONLINE/TEF); null = nenhuma. */
  idConfiguracaoIntegracao: number | null;
  /** Métodos marcados (ONLINE/TEF). */
  metodos: string[];
  /** Canais do cardápio onde a forma aparece (default: todos). */
  canais: Canal[];
  /** Pix estático (só LOCAL + PIX); chave vazia = sem QR Code. */
  pix: DadosPixEstatico;
  ordem: string;
  ativo: boolean;
}

export function AdminFormasPagamentoPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const [pagina, setPagina] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const { data, isLoading } = useAdminListarFormasPagamentoQuery({ idConfig, pagina: pagina + 1, tamanhoPagina });
  const { itens, total } = useUltimaPagina(data);
  const {
    data: formasSischef,
    isLoading: carregandoSischef,
    error: erroSischef,
  } = useAdminListarFormasPagamentoSischefQuery();
  const { data: configuracao } = useAdminObterConfiguracaoQuery(idConfig);
  const [criar, statusCriar] = useAdminCriarFormaPagamentoMutation();
  const [atualizar, statusAtualizar] = useAdminAtualizarFormaPagamentoMutation();

  const [editor, setEditor] = useState<EstadoEditor | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const { feedback, mostrarSucesso, mostrarErro, fecharFeedback } = useFeedback();

  // Configs de integração (ASAAS p/ ONLINE, ELGIN p/ TEF) da unidade — alimenta o combo.
  const tipoEditor = editor?.tipo;
  const ehIntegrado = tipoEditor === 'ONLINE' || tipoEditor === 'TEF';
  const {
    data: integracoes,
    isLoading: carregandoIntegracoes,
    refetch: recarregarIntegracoes,
  } = useAdminListarIntegracoesPagamentoQuery(
    { idConfig, tipo: tipoEditor === 'TEF' ? 'TEF' : 'ONLINE' },
    { skip: !ehIntegrado },
  );
  /** Combo vazio: falta cadastrar a integração no Sischef (ASAAS/ELGIN). */
  const semIntegracao = ehIntegrado && !carregandoIntegracoes && (integracoes?.length ?? 0) === 0;

  // Chave Pix só importa quando a forma ONLINE realmente oferece Pix ao cliente:
  // conta sem chave faz o ASAAS recusar a cobrança e o erro só apareceria no
  // checkout, para o cliente final. Cartão na mesma conta não é afetado.
  const idIntegracaoEditor = editor?.idConfiguracaoIntegracao ?? null;
  const conferirPix =
    tipoEditor === 'ONLINE' && idIntegracaoEditor != null && (editor?.metodos.includes('PIX') ?? false);
  const {
    data: diagnosticoPix,
    isFetching: verificandoPix,
    refetch: reverificarPix,
  } = useAdminDiagnosticoPixQuery(
    { idConfig, idConfiguracaoIntegracao: idIntegracaoEditor ?? 0 },
    { skip: !conferirPix },
  );

  function abrirCriar(): void {
    setErro(null);
    const proximaOrdem = itens.reduce((acc, f) => Math.max(acc, f.ordem), 0) + 1;
    setEditor({
      modo: 'criar',
      tipo: 'LOCAL',
      descricao: '',
      forma: 'DINHEIRO',
      codigo: '',
      idConfiguracaoIntegracao: null,
      metodos: [],
      canais: [...TODOS_CANAIS],
      pix: pixVazio(),
      ordem: String(proximaOrdem),
      ativo: true,
    });
  }

  function abrirEditar(forma: FormaPagamentoLoja): void {
    setErro(null);
    setEditor({
      modo: 'editar',
      formaPagamento: forma,
      tipo: forma.tipo,
      descricao: forma.descricao,
      forma: forma.forma,
      codigo: forma.codigo,
      idConfiguracaoIntegracao: forma.idConfiguracaoIntegracao ?? null,
      metodos: forma.metodos ? forma.metodos.split(',').filter(Boolean) : [],
      canais: parseCanais(forma.visibilidadeFlags),
      pix: forma.pixChave
        ? { pixChave: forma.pixChave, pixNomeRecebedor: forma.pixNomeRecebedor ?? '', pixCidade: forma.pixCidade ?? '' }
        : pixVazio(),
      ordem: String(forma.ordem),
      ativo: forma.ativo,
    });
  }

  /** Nome da loja já sugerido: é o que o lojista mais escreveria ali. */
  function pixVazio(): DadosPixEstatico {
    return { pixChave: '', pixNomeRecebedor: (configuracao?.nome ?? '').slice(0, MAX_NOME_RECEBEDOR_PIX), pixCidade: '' };
  }

  function fecharEditor(): void {
    setEditor(null);
    setErro(null);
  }

  async function confirmar(): Promise<void> {
    if (!editor) return;
    setErro(null);
    try {
      const ordemNum = Number(editor.ordem);
      const integrado = editor.tipo === 'ONLINE' || editor.tipo === 'TEF';
      const pixLocal = editor.tipo === 'LOCAL' && editor.forma === 'PIX' && !!editor.pix.pixChave.trim();
      const dados = {
        tipo: editor.tipo,
        forma: editor.forma,
        codigo: editor.codigo,
        descricao: editor.descricao.trim(),
        idConfiguracaoIntegracao: integrado ? editor.idConfiguracaoIntegracao : null,
        metodos: integrado ? editor.metodos.join(',') : null,
        visibilidadeFlags: montarVisibilidadeFlags(editor.canais),
        pixChave: pixLocal ? editor.pix.pixChave.trim() : null,
        pixNomeRecebedor: pixLocal ? editor.pix.pixNomeRecebedor.trim() : null,
        pixCidade: pixLocal ? editor.pix.pixCidade.trim() : null,
        ordem: Number.isFinite(ordemNum) ? ordemNum : undefined,
        ativo: editor.ativo,
      };
      if (editor.modo === 'criar') {
        await criar({ idConfiguracaoCardapio: idConfig, ...dados }).unwrap();
      } else {
        await atualizar({ id: editor.formaPagamento!.id, dados }).unwrap();
      }
      fecharEditor();
      mostrarSucesso(editor.modo === 'criar' ? 'Forma de pagamento criada' : 'Forma de pagamento atualizada');
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErro(msg ?? 'Falha ao salvar forma de pagamento');
    }
  }

  async function alternarAtivo(forma: FormaPagamentoLoja): Promise<void> {
    try {
      await atualizar({ id: forma.id, dados: { ativo: !forma.ativo } }).unwrap();
    } catch {
      // Falhava em silêncio: o switch voltava sozinho e o gestor não sabia por quê.
      mostrarErro('Não foi possível alterar a forma de pagamento. Tente de novo.');
    }
  }

  function descricaoSischef(codigo: string): string {
    const forma = formasSischef?.find((f) => String(f.id) === codigo);
    return forma ? forma.descricao : `#${codigo}`;
  }

  const salvando = statusCriar.isLoading || statusAtualizar.isLoading;
  const ehPixLocal = editor?.tipo === 'LOCAL' && editor.forma === 'PIX';
  const formas = [...itens].sort((a, b) => a.ordem - b.ordem);
  const podeSalvar =
    !!editor &&
    !!editor.descricao.trim() &&
    !!editor.codigo &&
    (editor?.canais.length ?? 0) > 0 &&
    (!ehIntegrado || (!!editor.idConfiguracaoIntegracao && editor.metodos.length > 0)) &&
    (!ehPixLocal || !editor.pix.pixChave.trim() || (!!editor.pix.pixNomeRecebedor.trim() && !!editor.pix.pixCidade.trim()));

  return (
    <>
      <HeaderTela
        titulo="Formas de pagamento"
        descricao="Métodos de pagamento aceitos pela loja na hora do pedido, associados às formas de pagamento do Sischef."
        acoes={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirCriar}
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Nova forma
          </Button>
        }
      />

      {erroSischef && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Não foi possível carregar as formas de pagamento do Sischef — o combo de associação ficará vazio.
          Verifique se o Sischef está acessível e recarregue a página.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell align="right" sx={{ width: 70 }}>
                  Ordem
                </TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Forma</TableCell>
                <TableCell>Forma no Sischef</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formas.map((forma) => (
                <TableRow key={forma.id} sx={{ opacity: forma.ativo ? 1 : 0.6 }}>
                  <TableCell align="right">{forma.ordem}</TableCell>
                  <TableCell>{forma.descricao}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={ROTULO_TIPO[forma.tipo] ?? forma.tipo}
                      color={forma.tipo === 'ONLINE' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{forma.forma}</TableCell>
                  <TableCell>{descricaoSischef(forma.codigo)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={forma.ativo ? 'Ativo' : 'Inativo'}
                      color={forma.ativo ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton onClick={() => abrirEditar(forma)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={forma.ativo ? 'Inativar' : 'Reativar'}>
                      <IconButton
                        onClick={() => alternarAtivo(forma)}
                        color={forma.ativo ? 'error' : 'success'}
                      >
                        {forma.ativo ? <DeleteOutlineIcon /> : <CheckCircleOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {formas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Nenhuma forma de pagamento cadastrada ainda.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <PaginacaoTabela
          total={total}
          pagina={pagina}
          tamanhoPagina={tamanhoPagina}
          aoMudarPagina={setPagina}
          aoMudarTamanho={(t) => {
            setTamanhoPagina(t);
            setPagina(0);
          }}
        />
      </Paper>

      <Dialog open={!!editor} onClose={fecharEditor} fullWidth maxWidth="sm">
        <DialogTitle>
          {editor?.modo === 'editar' ? 'Editar forma de pagamento' : 'Nova forma de pagamento'}
        </DialogTitle>
        <DialogContent>
          {editor && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Tipo do pagamento"
                value={editor.tipo}
                onChange={(e) =>
                  setEditor({
                    ...editor,
                    tipo: e.target.value as TipoPagamentoLoja,
                    // Trocar de tipo limpa config/métodos (combos diferentes por tipo).
                    idConfiguracaoIntegracao: null,
                    metodos: [],
                  })
                }
                fullWidth
              >
                <MenuItem value="LOCAL">Pagamento local</MenuItem>
                <MenuItem value="ONLINE">Pagamento online (ASAAS · delivery)</MenuItem>
                <MenuItem value="TEF">TEF maquininha (ELGIN · totem)</MenuItem>
              </TextField>

              {ehIntegrado && (
                <>
                  <TextField
                    select
                    label={editor.tipo === 'TEF' ? 'Configuração do TEF (ELGIN)' : 'Configuração do ASAAS'}
                    value={editor.idConfiguracaoIntegracao != null ? String(editor.idConfiguracaoIntegracao) : ''}
                    onChange={(e) =>
                      setEditor({
                        ...editor,
                        idConfiguracaoIntegracao: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    fullWidth
                    required
                    disabled={carregandoIntegracoes}
                    helperText={
                      carregandoIntegracoes
                        ? 'Carregando configurações…'
                        : semIntegracao
                          ? 'Nenhuma configuração encontrada — veja abaixo como cadastrar.'
                          : 'A configuração com o token é cadastrada no Sischef Retaguarda; aqui você apenas vincula.'
                    }
                  >
                    {(integracoes ?? []).map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.descricao}
                      </MenuItem>
                    ))}
                  </TextField>

                  {semIntegracao && (
                    <AlertaSemIntegracaoPagamento
                      tipo={editor.tipo === 'TEF' ? 'TEF' : 'ONLINE'}
                      aoAtualizar={() => void recarregarIntegracoes()}
                    />
                  )}

                  <Box>
                    <FormLabel component="legend" sx={{ fontSize: 14, mb: 0.5 }}>
                      Métodos oferecidos ao cliente
                    </FormLabel>
                    <FormGroup row>
                      {METODOS_POR_TIPO[editor.tipo === 'TEF' ? 'TEF' : 'ONLINE'].map((m) => (
                        <FormControlLabel
                          key={m.valor}
                          control={
                            <Checkbox
                              checked={editor.metodos.includes(m.valor)}
                              onChange={(e) =>
                                setEditor({
                                  ...editor,
                                  metodos: e.target.checked
                                    ? [...editor.metodos, m.valor]
                                    : editor.metodos.filter((x) => x !== m.valor),
                                })
                              }
                            />
                          }
                          label={m.rotulo}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {conferirPix && diagnosticoPix && (
                    <AlertaChavePix
                      situacao={diagnosticoPix.situacao}
                      verificando={verificandoPix}
                      aoVerificarDeNovo={() => void reverificarPix()}
                    />
                  )}

                  {/* O ONLINE depende do webhook do ASAAS; o TEF, do app local no totem. */}
                  {editor.tipo === 'ONLINE' ? <InstrucoesWebhookAsaas idConfig={idConfig} /> : <InstrucoesTefElgin />}
                </>
              )}

              <TextField
                label="Descrição"
                value={editor.descricao}
                onChange={(e) => setEditor({ ...editor, descricao: e.target.value })}
                fullWidth
                required
                placeholder="Ex.: Cartão de crédito na entrega"
                helperText="Nome exibido ao cliente no checkout."
              />

              <TextField
                select
                label="Forma"
                value={editor.forma}
                onChange={(e) => setEditor({ ...editor, forma: e.target.value as FormaPagamentoOpcao })}
                fullWidth
              >
                {FORMAS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>

              {ehPixLocal && (
                <CamposPixEstatico
                  dados={editor.pix}
                  aoMudar={(pix) => setEditor({ ...editor, pix })}
                  temWhatsapp={!!configuracao?.whatsapp?.trim()}
                />
              )}

              <TextField
                select
                label="Associar à forma de pagamento no Sischef"
                value={editor.codigo}
                onChange={(e) => setEditor({ ...editor, codigo: e.target.value })}
                fullWidth
                required
                disabled={carregandoSischef}
                helperText={
                  carregandoSischef
                    ? 'Carregando formas do Sischef…'
                    : 'Forma de pagamento que o pedido vai usar dentro do Sischef.'
                }
              >
                {(formasSischef ?? []).map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.descricao}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Ordem"
                value={editor.ordem}
                onChange={(e) => setEditor({ ...editor, ordem: e.target.value.replace(/[^0-9]/g, '') })}
                fullWidth
                type="number"
                helperText="Posição na listagem do checkout (menor aparece primeiro)."
              />

              <Box>
                <FormLabel component="legend" sx={{ fontSize: 14, mb: 0.5 }}>
                  Canais do cardápio
                </FormLabel>
                <FormGroup row>
                  {CANAIS.map((c) => (
                    <FormControlLabel
                      key={c.valor}
                      control={
                        <Checkbox
                          checked={editor.canais.includes(c.valor)}
                          onChange={(e) =>
                            setEditor({
                              ...editor,
                              canais: e.target.checked
                                ? [...editor.canais, c.valor]
                                : editor.canais.filter((x) => x !== c.valor),
                            })
                          }
                        />
                      }
                      label={c.rotulo}
                    />
                  ))}
                </FormGroup>
                <Typography
                  variant="caption"
                  color={editor.canais.length === 0 ? 'error' : 'text.secondary'}
                >
                  {editor.canais.length === 0
                    ? 'Selecione ao menos um canal.'
                    : 'Desmarque os canais onde esta forma NÃO deve aparecer (ex.: dinheiro fora do totem).'}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={editor.ativo}
                    onChange={(e) => setEditor({ ...editor, ativo: e.target.checked })}
                  />
                }
                label={editor.ativo ? 'Ativo' : 'Inativo'}
              />

              {erro && <Alert severity="error">{erro}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharEditor} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmar} disabled={!podeSalvar || salvando}>
            {salvando ? <CircularProgress size={20} /> : editor?.modo === 'editar' ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarFeedback feedback={feedback} aoFechar={fecharFeedback} />
    </>
  );
}
